import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { TabNavigation } from '../components/TabNavigation'
import { CheckIcon, XMarkIcon, ClockIcon, UserIcon } from '@heroicons/react/24/outline'

interface StudentAttendance {
  id: string
  name: string
  student_code: string
  grade: string
  attendance: 'present' | 'absent' | 'late' | 'not_marked'
  notes?: string
}

interface AttendanceSession {
  date: string
  students: StudentAttendance[]
  isSubmitted: boolean
  submittedAt?: string
}

export function AdminAttendancePage() {
  const navigate = useNavigate()
  const today = new Date().toISOString().split('T')[0]

  // Helper function to check if date is weekend (Saturday=6 or Sunday=0)
  const isWeekend = (dateString: string) => {
    const date = new Date(dateString)
    const dayOfWeek = date.getDay()
    return dayOfWeek === 0 || dayOfWeek === 6 // Sunday=0, Saturday=6
  }

  // Find the most recent weekend date
  const getMostRecentWeekend = () => {
    const today = new Date()
    const dayOfWeek = today.getDay()
    
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      // Today is weekend
      return today.toISOString().split('T')[0]
    } else if (dayOfWeek === 1) {
      // Monday - go back to Sunday
      const sunday = new Date(today)
      sunday.setDate(today.getDate() - 1)
      return sunday.toISOString().split('T')[0]
    } else {
      // Tuesday-Friday - go back to Saturday
      const saturday = new Date(today)
      saturday.setDate(today.getDate() - (dayOfWeek - 6))
      return saturday.toISOString().split('T')[0]
    }
  }
  
  const [session, setSession] = useState<AttendanceSession>({
    date: today,
    isSubmitted: false,
    students: [
      {
        id: '1',
        name: 'เด็กหญิงใสใส สดใส',
        student_code: 'STU-001',
        grade: 'ป.3',
        attendance: 'not_marked'
      },
      {
        id: '2', 
        name: 'เด็กชายกิตติ์ สุขใส',
        student_code: 'STU-002',
        grade: 'ป.3',
        attendance: 'not_marked'
      },
      {
        id: '3',
        name: 'เด็กหญิงมินิ คิวท์',
        student_code: 'STU-003', 
        grade: 'ป.3',
        attendance: 'not_marked'
      },
      {
        id: '4',
        name: 'เด็กชายแม็กซ์ แฮปปี้',
        student_code: 'STU-004',
        grade: 'ป.3',
        attendance: 'not_marked'
      },
      {
        id: '5',
        name: 'เด็กหญิงลูน่า เบลล์',
        student_code: 'STU-005',
        grade: 'ป.3', 
        attendance: 'not_marked'
      },
      {
        id: '6',
        name: 'เด็กชายเอลิซ สมาร์ท',
        student_code: 'STU-006',
        grade: 'ป.3',
        attendance: 'not_marked'
      }
    ]
  })

  const [selectedDate, setSelectedDate] = useState(getMostRecentWeekend())
  const [isSubmitting, setIsSubmitting] = useState(false)

  const formatThaiDate = (dateString: string) => {
    const date = new Date(dateString)
    const thaiYear = date.getFullYear() + 543
    const months = [
      'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
      'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
    ]
    const days = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']
    
    return `วัน${days[date.getDay()]}ที่ ${date.getDate()} ${months[date.getMonth()]} ${thaiYear}`
  }

  const updateAttendance = (studentId: string, attendance: 'present' | 'absent' | 'late') => {
    setSession(prev => ({
      ...prev,
      students: prev.students.map(student =>
        student.id === studentId 
          ? { ...student, attendance }
          : student
      )
    }))
  }

  const updateNotes = (studentId: string, notes: string) => {
    setSession(prev => ({
      ...prev,
      students: prev.students.map(student =>
        student.id === studentId 
          ? { ...student, notes }
          : student
      )
    }))
  }

  const markAllPresent = () => {
    setSession(prev => ({
      ...prev,
      students: prev.students.map(student => ({
        ...student,
        attendance: 'present'
      }))
    }))
  }

  const handleSubmitAttendance = async () => {
    setIsSubmitting(true)
    
    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      setSession(prev => ({
        ...prev,
        isSubmitted: true,
        submittedAt: new Date().toISOString()
      }))

      // Navigate to success page
      navigate('/admin/attendance/success', {
        state: {
          date: selectedDate,
          totalStudents: session.students.length,
          presentCount: session.students.filter(s => s.attendance === 'present').length,
          absentCount: session.students.filter(s => s.attendance === 'absent').length,
          lateCount: session.students.filter(s => s.attendance === 'late').length
        }
      })
      
    } catch (error) {
      console.error('Failed to submit attendance:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getAttendanceStats = () => {
    const total = session.students.length
    const present = session.students.filter(s => s.attendance === 'present').length
    const absent = session.students.filter(s => s.attendance === 'absent').length
    const late = session.students.filter(s => s.attendance === 'late').length
    const notMarked = session.students.filter(s => s.attendance === 'not_marked').length
    
    return { total, present, absent, late, notMarked }
  }

  const stats = getAttendanceStats()
  const canSubmit = stats.notMarked === 0 && !session.isSubmitted

  const getAttendanceIcon = (attendance: string) => {
    switch (attendance) {
      case 'present':
        return <CheckIcon className="h-5 w-5 text-green-600" />
      case 'absent':
        return <XMarkIcon className="h-5 w-5 text-red-600" />
      case 'late':
        return <ClockIcon className="h-5 w-5 text-yellow-600" />
      default:
        return <UserIcon className="h-5 w-5 text-gray-400" />
    }
  }

  const getAttendanceColor = (attendance: string) => {
    switch (attendance) {
      case 'present':
        return 'bg-green-50 border-green-200'
      case 'absent':
        return 'bg-red-50 border-red-200'
      case 'late':
        return 'bg-yellow-50 border-yellow-200'
      default:
        return 'bg-gray-50 border-gray-200'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="เช็คชื่อนักเรียน"
        subtitle={formatThaiDate(selectedDate)}
      />

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Date Selector */}
        <div className="bg-white rounded-lg border p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            เลือกวันที่ (เฉพาะเสาร์-อาทิตย์)
          </label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => {
              if (isWeekend(e.target.value)) {
                setSelectedDate(e.target.value)
              } else {
                alert('กรุณาเลือกวันเสาร์หรือวันอาทิตย์เท่านั้น')
              }
            }}
            className="input-thai"
            max={today}
          />
          <p className="text-xs text-gray-500 mt-1">
            โรงเรียนเปิดสอนเฉพาะวันเสาร์และวันอาทิตย์เท่านั้น
          </p>
        </div>

        {/* Attendance Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg border p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-600">ทั้งหมด</div>
          </div>
          <div className="bg-white rounded-lg border p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{stats.present}</div>
            <div className="text-sm text-gray-600">มาเรียน</div>
          </div>
          <div className="bg-white rounded-lg border p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{stats.absent}</div>
            <div className="text-sm text-gray-600">ขาดเรียน</div>
          </div>
          <div className="bg-white rounded-lg border p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{stats.late}</div>
            <div className="text-sm text-gray-600">มาสาย</div>
          </div>
          <div className="bg-white rounded-lg border p-4 text-center">
            <div className="text-2xl font-bold text-gray-400">{stats.notMarked}</div>
            <div className="text-sm text-gray-600">ยังไม่เช็ค</div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-3">
          <button
            onClick={markAllPresent}
            className="btn-secondary"
            disabled={session.isSubmitted}
          >
            ✅ เช็คมาครบทุกคน
          </button>
        </div>

        {/* Students List */}
        <div className="space-y-3">
          {session.students.map((student) => (
            <div 
              key={student.id}
              className={`border rounded-lg p-4 transition-colors ${getAttendanceColor(student.attendance)}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  {getAttendanceIcon(student.attendance)}
                  <div>
                    <h3 className="font-medium text-gray-900">{student.name}</h3>
                    <p className="text-sm text-gray-600">{student.student_code}</p>
                  </div>
                </div>
                <span className="text-sm text-gray-500">{student.grade}</span>
              </div>

              {/* Attendance Buttons */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <button
                  onClick={() => updateAttendance(student.id, 'present')}
                  disabled={session.isSubmitted}
                  className={`py-2 px-3 rounded text-sm font-medium transition-colors ${
                    student.attendance === 'present'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-green-100'
                  }`}
                >
                  ✅ มาเรียน
                </button>
                <button
                  onClick={() => updateAttendance(student.id, 'late')}
                  disabled={session.isSubmitted}
                  className={`py-2 px-3 rounded text-sm font-medium transition-colors ${
                    student.attendance === 'late'
                      ? 'bg-yellow-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-yellow-100'
                  }`}
                >
                  ⏰ มาสาย
                </button>
                <button
                  onClick={() => updateAttendance(student.id, 'absent')}
                  disabled={session.isSubmitted}
                  className={`py-2 px-3 rounded text-sm font-medium transition-colors ${
                    student.attendance === 'absent'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-red-100'
                  }`}
                >
                  ❌ ขาดเรียน
                </button>
              </div>

              {/* Notes */}
              <textarea
                placeholder="หมายเหตุ (ถ้ามี)"
                value={student.notes || ''}
                onChange={(e) => updateNotes(student.id, e.target.value)}
                disabled={session.isSubmitted}
                className="w-full p-2 border border-gray-300 rounded text-sm resize-none"
                rows={2}
              />
            </div>
          ))}
        </div>

        {/* Submit Button */}
        <div className="sticky bottom-4 bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {stats.notMarked > 0 ? (
                `ยังไม่เช็คชื่อ ${stats.notMarked} คน`
              ) : (
                'เช็คชื่อครบทุกคนแล้ว'
              )}
            </div>
            <button
              onClick={handleSubmitAttendance}
              disabled={!canSubmit || isSubmitting}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                canSubmit && !isSubmitting
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? '⏳ กำลังบันทึก...' : '💾 บันทึกการเช็คชื่อ'}
            </button>
          </div>
        </div>

        {session.isSubmitted && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800 text-center">
              ✅ บันทึกการเช็คชื่อเรียบร้อยแล้ว
            </p>
          </div>
        )}
      </div>

      <TabNavigation activeTab="attendance" />
    </div>
  )
}